import { OptionLabel } from '../caption'
import { FieldShell } from '../FieldShell'
import { labelToText } from '../richText'
import type { FieldComponentProps } from '../rendererContext'
import type { EnumConfig } from '../types'
import { OptionsStatus } from './OptionsStatus'
import { optionKey, valuesEqual, visibleOptions } from './optionUtils'
import { useControlMeta } from './useControlMeta'
import { useResolvedOptions } from './useResolvedOptions'

export function RadioField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const config = field.config as EnumConfig | undefined
  const { options, loading, error } = useResolvedOptions(field)
  const shown = visibleOptions(options, (v) => valuesEqual(v, value))

  if (field.display_only) {
    const selected = options.find((opt) => valuesEqual(opt.value, value))
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <div id={meta.id} className="form-control-plaintext">
          {selected ? labelToText(selected.label) : value == null ? '' : String(value)}
        </div>
      </FieldShell>
    )
  }

  // render_as: "buttons" → a Bootstrap button-group toggle instead of radios.
  if (config?.render_as === 'buttons') {
    return (
      <FieldShell field={field} path={path} errors={errors} groupLabel>
        <div
          className="btn-group"
          role="group"
          id={meta.id}
          aria-labelledby={meta.labelId}
          aria-describedby={meta.describedBy}
        >
          {shown.map((opt) => {
            const selected = valuesEqual(opt.value, value)
            return (
              <button
                key={optionKey(opt.value)}
                type="button"
                className={`btn ${selected ? 'btn-primary' : 'btn-outline-primary'}`}
                aria-pressed={selected}
                disabled={meta.disabled}
                onClick={() => {
                  meta.setFieldValue(path, opt.value)
                  meta.notifyBlur(path)
                }}
              >
                {labelToText(opt.label)}
              </button>
            )
          })}
        </div>
        <OptionsStatus loading={loading} error={error} />
      </FieldShell>
    )
  }

  return (
    <FieldShell field={field} path={path} errors={errors} groupLabel>
      <div
        role="radiogroup"
        id={meta.id}
        aria-labelledby={meta.labelId}
        aria-describedby={meta.describedBy}
        aria-required={meta.ariaRequired}
      >
        {shown.map((opt, i) => {
          const optId = `${meta.id}-${i}`
          // Input nested inside its <label> (text in a sibling <span>), wrapped in a
          // `.radio` container — the RX form builder / forms-universal pattern the
          // checkbox fields already use. The app's `label { font-weight: 700 }` element
          // selector is only undone by `.checkbox, .radio { label { … } }` and
          // `label:not(.btn) span { … }`, so the sibling Bootstrap `form-check` markup
          // rendered option text bold and skipped the custom radio painting.
          const labelClass = `${meta.classNames.checkLabel ?? 'label--flex'}${meta.disabled ? ' label--disabled' : ''}`
          return (
            <div className={meta.classNames.radioContainer ?? 'radio'} key={optionKey(opt.value)}>
              <label className={labelClass}>
                <input
                  id={optId}
                  type="radio"
                  name={meta.id}
                  className={`${meta.classNames.checkInput ?? ''}${meta.hasErrors ? ' is-invalid' : ''}`.trim() || undefined}
                  checked={valuesEqual(opt.value, value)}
                  disabled={meta.disabled}
                  onChange={() => {
                    meta.setFieldValue(path, opt.value)
                    meta.notifyBlur(path)
                  }}
                />
                <OptionLabel label={opt.label} />
              </label>
            </div>
          )
        })}
      </div>
      <OptionsStatus loading={loading} error={error} />
    </FieldShell>
  )
}
